/**
 * Role-based access control helper
 * 
 * This utility provides functions to check user permissions based on their roles.
 * Roles are stored in the user object as an array.
 */

export type UserRole = 'super_admin' | 'admin' | 'manager' | 'dock_coordinator' | 'carrier' | 'transport' | 'spotter' | 'user' | 'viewer';

export interface User {
  id?: string;
  email?: string;
  roles?: string[];
  canAccessUserManagement?: boolean;
}

/**
 * Normalize a role name: lowercase and replace spaces with underscores
 */
const normalizeRole = (role: string): string => {
  return role.toLowerCase().replace(/\s+/g, '_');
};

/**
 * Check if a user has a specific role
 */
export const hasRole = (user: User | null | undefined, role: UserRole): boolean => {
  if (!user || !user.roles || !Array.isArray(user.roles)) {
    return false;
  }
  const normalizedRole = normalizeRole(role);
  const normalizedUserRoles = user.roles.map(r => normalizeRole(r));
  return normalizedUserRoles.includes(normalizedRole);
};

/**
 * Check if a user has any of the specified roles
 */
export const hasAnyRole = (user: User | null | undefined, roles: UserRole[]): boolean => {
  if (!user || !user.roles || !Array.isArray(user.roles)) {
    return false;
  }
  const normalizedRoles = roles.map(r => normalizeRole(r));
  const normalizedUserRoles = user.roles.map(r => normalizeRole(r));
  return normalizedRoles.some(role => normalizedUserRoles.includes(role));
};

/**
 * Check if a user is a super admin
 */
export const isSuperAdmin = (user: User | null | undefined): boolean => {
  return hasRole(user, 'super_admin');
};

/**
 * Check if a user is an admin (including super admin)
 */
export const isAdmin = (user: User | null | undefined): boolean => {
  return hasAnyRole(user, ['super_admin', 'admin']);
};

/**
 * Check if a user is a dock coordinator (includes manager, admin, and super admin)
 */
export const isDockCoordinator = (user: User | null | undefined): boolean => {
  return hasAnyRole(user, ['dock_coordinator', 'manager', 'admin', 'super_admin']);
};

/**
 * Check if a user is a carrier
 */
export const isCarrier = (user: User | null | undefined): boolean => {
  return hasRole(user, 'carrier');
};

/**
 * Check if a user is a spotter
 */
export const isSpotter = (user: User | null | undefined): boolean => {
  return hasRole(user, 'spotter');
};

/**
 * Check if a user is a transport user
 */
export const isTransport = (user: User | null | undefined): boolean => {
  return hasRole(user, 'transport');
};

/**
 * Check if a user has read-only shipments access (carrier or transport)
 */
export const isReadOnlyShipments = (user: User | null | undefined): boolean => {
  return isCarrier(user) || isTransport(user);
};

/**
 * Check if a user can access task app (spotter or dock coordinator+)
 */
export const canAccessTaskApp = (user: User | null | undefined): boolean => {
  return hasAnyRole(user, ['spotter', 'dock_coordinator', 'manager', 'admin', 'super_admin']);
};

export interface NavPermissions {
  carrierTrailers: boolean;
  transportTrailers: boolean;
  dashboard: boolean;
  shipments: boolean;
  driverMgt: boolean;
  driverComms: boolean;
  dockMgt: boolean;
  yardSpots: boolean;
  trailerHistory: boolean;
  whiteboard: boolean;
  taskHistory: boolean;
  taskApp: boolean;
  trailers: boolean;
  facilities: boolean;
}

/**
 * Compute which nav items a user can see based on their role and feature flags.
 * Single source of truth for sidebar visibility — no scattered boolean logic.
 */
export const getNavPermissions = (
  user: User | null | undefined,
  opts: { ymsEnabled: boolean }
): NavPermissions => {
  const carrier = isCarrier(user);
  const transport = isTransport(user);
  const dockCoord = isDockCoordinator(user);
  const { ymsEnabled } = opts;

  // Restricted roles only see their own section
  if (carrier) {
    return {
      carrierTrailers: true,
      transportTrailers: false,
      dashboard: false,
      shipments: true,
      driverMgt: false,
      driverComms: false,
      dockMgt: false,
      yardSpots: false,
      trailerHistory: false,
      whiteboard: false,
      taskHistory: false,
      taskApp: false,
      trailers: false,
      facilities: false,
    };
  }

  if (transport) {
    return {
      carrierTrailers: false,
      transportTrailers: true,
      dashboard: false,
      shipments: true,
      driverMgt: false,
      driverComms: false,
      dockMgt: false,
      yardSpots: false,
      trailerHistory: false,
      whiteboard: false,
      taskHistory: false,
      taskApp: false,
      trailers: false,
      facilities: false,
    };
  }

  // Standard roles
  const yms = dockCoord && ymsEnabled;
  return {
    carrierTrailers: false,
    transportTrailers: false,
    dashboard: ymsEnabled,
    shipments: dockCoord,
    driverMgt: yms,
    driverComms: yms,
    dockMgt: yms,
    yardSpots: yms,
    trailerHistory: yms,
    whiteboard: yms,
    taskHistory: yms,
    taskApp: yms,
    trailers: yms,
    facilities: true,
  };
};

